import { Alert, AlertIcon, Box, Button, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiRotateCcw, FiXCircle } from "react-icons/fi";
import type { BuyerBidDetailDto } from "../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { RejectionDetails } from "../../../common/rejection/RejectionDetails";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { SectionCard } from "../../sourcing-events/portal/view-single/SectionCard";
import { useBidRejectionDecision } from "./useBidRejectionDecision";

export const RejectedSummary = ({
  detail,
  onViewDetails,
}: {
  detail: BuyerBidDetailDto;
  onViewDetails?: () => void;
}) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const rejection = detail.bid.rejection;
  const rejectedMeta = formatRejectedMeta({
    rejectedAt: rejection?.rejectedAt,
    rejectedBy: rejection?.rejectedBy,
    displayDateTime,
    t,
  });

  return (
    <SectionCard
      icon={FiXCircle}
      title={t("Avvist leverandør")}
      borderColor="red.100"
      headerBg="red.50"
      headerBorderColor="red.100"
      iconColor="red.600"
    >
      <Stack spacing="3">
        {rejectedMeta ? (
          <Text fontSize="sm" color="smMuted">
            {rejectedMeta}
          </Text>
        ) : null}

        {rejection?.grounds?.length ? (
          <Text fontSize="sm" color="smText">
            {t("{{count}} lovhjemmel valgt", {
              count: rejection.grounds.length,
            })}
          </Text>
        ) : reasonPreview(rejection?.reason) ? (
          <Text fontSize="sm" color="smText" noOfLines={2}>
            {reasonPreview(rejection?.reason)}
          </Text>
        ) : (
          <Text fontSize="sm" color="smMuted">
            {t("Det er ikke lagret detaljer for denne avvisningen.")}
          </Text>
        )}

        {onViewDetails ? (
          <Button
            alignSelf="flex-start"
            size="sm"
            variant="link"
            colorScheme="teal"
            rightIcon={<FiArrowRight />}
            onClick={onViewDetails}
          >
            {t("Se avvisningsdetaljer")}
          </Button>
        ) : null}

        <UndoRejection detail={detail} />
      </Stack>
    </SectionCard>
  );
};

const UndoRejection = ({ detail }: { detail: BuyerBidDetailDto }) => {
  const { t } = useTranslation();
  const rejection = useBidRejectionDecision(detail);
  const { show, modal } = useConfirmationModal();
  const [failure, setFailure] = useState<unknown>(null);

  const confirm = () =>
    show({
      title: String(t("Undo the rejection?")),
      description: (
        <Text>
          {t(
            "The tender from {{supplier}} counts as delivered again. The supplier is back in any open round and can be invited to later rounds.",
            { supplier: detail.ownerName },
          )}
        </Text>
      ),
      confirmButtonText: String(t("Undo the rejection")),
      cancelButtonText: String(t("Cancel")),
      confirmButtonColor: "red",
      size: "lg",
      onConfirmed: async () => {
        setFailure(null);
        const outcome = await rejection.revokeOutcome();
        if (!outcome.ok) setFailure(outcome.error);
      },
    });

  return (
    <>
      {modal}
      {failure != null && (
        <Alert status="warning" rounded="md" fontSize="sm">
          <AlertIcon />
          {errorCodeOf(failure) === QUALIFICATION_CLOSED
            ? t("The procedure has moved past «Published», so the rejection can no longer be undone here.")
            : t("The rejection could not be undone. Try again.")}
        </Alert>
      )}
      {rejection.canRevoke && (
        <Button
          alignSelf="flex-start"
          size="sm"
          variant="outline"
          leftIcon={<FiRotateCcw />}
          onClick={confirm}
          isLoading={rejection.isLoading}
        >
          {t("Undo the rejection")}
        </Button>
      )}
    </>
  );
};

const QUALIFICATION_CLOSED = "zz_BidApiErrorCode_QualificationClosed";

const errorCodeOf = (error: unknown): string | undefined =>
  (error as { data?: { errorCode?: string } } | null)?.data?.errorCode;

export const RejectionDetailsSection = ({
  detail,
}: {
  detail: BuyerBidDetailDto;
}) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const rejection = detail.bid.rejection;
  const rejectedMeta = formatRejectedMeta({
    rejectedAt: rejection?.rejectedAt,
    rejectedBy: rejection?.rejectedBy,
    displayDateTime,
    t,
  });

  if (!rejection) return null;

  return (
    <Box id="bid-rejection-details" scrollMarginTop="6">
      <SectionCard icon={FiXCircle} title={t("Avvisningsdetaljer")}>
        <Stack spacing="3">
          {rejectedMeta ? (
            <Text fontSize="sm" color="smMuted">
              {rejectedMeta}
            </Text>
          ) : null}
          <RejectionDetails
            grounds={rejection.grounds}
            reason={rejection.reason}
            message={rejection.message}
            attachments={rejection.attachments}
            attachmentIds={rejection.attachmentIds}
          />
        </Stack>
      </SectionCard>
    </Box>
  );
};

const reasonPreview = (reason?: string | null) =>
  reason
    ?.replace(/[*_#`~>]/g, "")
    .replace(/\s+/g, " ")
    .trim() || null;

const formatRejectedMeta = ({
  rejectedAt,
  rejectedBy,
  displayDateTime,
  t,
}: {
  rejectedAt?: string | null;
  rejectedBy?: string | null;
  displayDateTime: (value: string) => string;
  t: TFunction;
}) => {
  if (rejectedAt && rejectedBy) {
    return t("Avvist {{date}} av {{person}}", {
      date: displayDateTime(rejectedAt),
      person: rejectedBy,
    });
  }
  if (rejectedAt) {
    return t("Avvist {{date}}", { date: displayDateTime(rejectedAt) });
  }
  if (rejectedBy) {
    return t("Avvist av {{person}}", { person: rejectedBy });
  }
  return null;
};
